import { FC, useEffect, useState } from 'react';
import { Portal } from './Portal';
import { useAppConfig } from '@/context/AppConfigContext';

interface FloatingVeraBadgeProps {
  className?: string;
}

export const FloatingVeraBadge: FC<FloatingVeraBadgeProps> = ({ className = '' }) => {
  const { themeMode } = useAppConfig();
  const isDarkTheme = themeMode === 'dark';
  
  // State to track initial animation and hover
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  // State to track if we should hide the badge
  const [shouldHideBadge, setShouldHideBadge] = useState(false);
  
  useEffect(() => {
    // Function to check if we should hide the badge based on what's rendered
    function checkIfMemberDetailsPage() {
      // Find all page headings
      const h1Elements = document.querySelectorAll('h1');
      
      // Find content in the headings that indicates "Member Details"
      let isMemberDetailsPage = false;
      h1Elements.forEach(h1 => {
        if (h1.textContent && h1.textContent.includes('Member Details')) {
          isMemberDetailsPage = true;
        }
      });
      
      // If specifically looking at a member (via selected parameter)
      const urlParams = new URLSearchParams(window.location.search);
      const hasSelectedMember = urlParams.has('selected');
      
      // We'll hide the badge on any page that's not the main member list
      // This is a new, simpler approach that's more likely to work
      setShouldHideBadge(isMemberDetailsPage || hasSelectedMember);
    }
    
    // Initial check with a delay to allow DOM to load
    setTimeout(checkIfMemberDetailsPage, 500);
    
    // Set up an interval to keep checking (for SPAs that don't fully reload)
    const intervalId = setInterval(checkIfMemberDetailsPage, 1000);
    
    // Clean up when component unmounts
    return () => {
      clearInterval(intervalId);
    };
  }, []);
  
  // Show the badge after a short delay for a smooth entrance
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 500);
    return () => clearTimeout(timer);
  }, []);
  
  // Don't render if we should hide
  if (shouldHideBadge) {
    return null;
  }

  return (
    <Portal>
      <div
        className={`fixed z-50 transition-all duration-300 ${className} ${
          isVisible ? 'opacity-70 bottom-4' : 'opacity-0 bottom-0'
        } ${isHovered ? 'opacity-100 scale-105' : ''} right-4 hover:opacity-100`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0.25rem 0.75rem',
          borderRadius: '9999px',
          fontSize: '0.75rem',
          backgroundColor: isDarkTheme 
            ? 'rgba(255, 255, 255, 0.25)'
            : 'rgb(241, 245, 249, 0.85)',
          color: isDarkTheme 
            ? 'white' 
            : 'rgb(71, 85, 105)',
          border: isDarkTheme
            ? '1px solid rgba(255, 255, 255, 0.3)'
            : 'none',
          boxShadow: isDarkTheme
            ? '0 1px 2px rgba(0, 0, 0, 0.05)'
            : '0 1px 3px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(8px)',
          transform: 'translateZ(0)',
          transition: 'all 0.2s ease-in-out',
        }}
      >
        <span>Powered by VeraScore™ AI</span>
      </div>
    </Portal>
  );
}; 